import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/utils/dateFormat";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DollarSign, Check, X, Users, Building2, RefreshCw } from "lucide-react";

const API = '/api';

export default function Payments() {
  const { isAdmin, getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [entries, setEntries] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [loading, setLoading] = useState(true);
  const [sponsorDialogOpen, setSponsorDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedSponsor, setSelectedSponsor] = useState("");

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchEntries();
      fetchRounds();
    }
  }, [selectedEvent]);

  const fetchInitialData = async () => {
    try {
      const t = Date.now();
      const [eventsRes, sponsorsRes] = await Promise.all([
        axios.get(`${API}/events?t=${t}`),
        axios.get(`${API}/sponsors?t=${t}`),
      ]);
      setEvents(eventsRes.data);
      setSponsors(sponsorsRes.data);
      if (eventsRes.data.length > 0) {
        setSelectedEvent(eventsRes.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/entries?event_id=${selectedEvent}&t=${Date.now()}`);
      setEntries(res.data);
    } catch (error) {
      toast.error("Failed to fetch entries");
    }
  };

  const fetchRounds = async () => {
    try {
      // Fetch all rounds for this event directly using event_id filter
      const res = await axios.get(`${API}/rounds?event_id=${selectedEvent}&t=${Date.now()}`);
      setRounds(res.data);
    } catch (error) {
      console.error("Failed to fetch rounds:", error);
    }
  };

  const togglePayment = async (entryId, currentPaid) => {
    try {
      await axios.put(`${API}/entries/${entryId}/payment`, {
        paid: !currentPaid,
        paid_by_sponsor: false,
        sponsor_id: null,
      }, { headers: getAuthHeader() });
      toast.success(currentPaid ? "Payment marked as unpaid" : "Payment confirmed!");
      setTimeout(() => fetchEntries(), 100);
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const openSponsorDialog = (entry) => {
    setSelectedEntry(entry);
    setSelectedSponsor(entry.sponsor_id || "");
    setSponsorDialogOpen(true);
  };

  const markSponsorPaid = async () => {
    if (!selectedEntry || !selectedSponsor) return;
    
    try {
      await axios.put(`${API}/entries/${selectedEntry.id}/payment`, {
        paid: true,
        paid_by_sponsor: true,
        sponsor_id: selectedSponsor,
      }, { headers: getAuthHeader() });
      toast.success("Marked as paid by sponsor");
      setSponsorDialogOpen(false);
      fetchEntries();
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  const getSponsorName = (sponsorId) => {
    const sponsor = sponsors.find(s => s.id === sponsorId);
    return sponsor?.name || "Unknown Sponsor";
  };

  const selectedEventData = events.find((e) => e.id === selectedEvent);
  
  // Calculate totals from rounds (fee tracking)
  const paidRounds = rounds.filter((r) => r.fee_paid);
  const unpaidRounds = rounds.filter((r) => !r.fee_paid);
  const sponsorPaidRounds = rounds.filter((r) => r.paid_by_sponsor);
  
  // Sum actual fee amounts from rounds
  const totalCollected = paidRounds.reduce((sum, r) => sum + parseFloat(r.fee_amount || 0), 0);
  const totalOutstanding = unpaidRounds.reduce((sum, r) => sum + parseFloat(r.fee_amount || 0), 0);
  const sponsorTotal = sponsorPaidRounds.reduce((sum, r) => sum + parseFloat(r.fee_amount || 0), 0);
  
  // Entry-based totals for backwards compatibility
  const paidEntries = entries.filter((e) => e.paid);
  const unpaidEntries = entries.filter((e) => !e.paid);
  const sponsorPaidEntries = entries.filter((e) => e.paid_by_sponsor);

  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Payment tracking is only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="payments-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="payments-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            Payments
          </h1>
          <p className="text-slate-500 mt-1">Track entry fee payments</p>
        </div>

        <div className="w-full sm:w-64">
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger data-testid="payment-event-select">
              <SelectValue placeholder="Select event" />
            </SelectTrigger>
            <SelectContent>
              {events.map((event) => (
                <SelectItem key={event.id} value={event.id}>
                  {event.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Payment Stats */}
      {selectedEventData && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 uppercase tracking-wider">Entry Fee</p>
              <p className="font-mono text-2xl font-bold text-[#1B3B6F]" data-testid="entry-fee">
                ${selectedEventData.entry_fee}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-slate-500 uppercase tracking-wider">Total Rounds</p>
              <p className="font-mono text-2xl font-bold text-slate-700" data-testid="total-rounds">
                {rounds.length}
              </p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-green-700 uppercase tracking-wider">Collected</p>
              <p className="font-mono text-2xl font-bold text-green-700" data-testid="collected-amount">
                ${totalCollected.toFixed(2)}
              </p>
              <p className="text-xs text-green-600">{paidRounds.length} rounds</p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-orange-700 uppercase tracking-wider">Outstanding</p>
              <p className="font-mono text-2xl font-bold text-orange-600" data-testid="outstanding-amount">
                ${totalOutstanding.toFixed(2)}
              </p>
              <p className="text-xs text-orange-600">{unpaidRounds.length} rounds</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <p className="text-sm text-purple-700 uppercase tracking-wider">Sponsor Paid</p>
              <p className="font-mono text-2xl font-bold text-purple-700" data-testid="sponsor-amount">
                ${sponsorTotal.toFixed(2)}
              </p>
              <p className="text-xs text-purple-600">{sponsorPaidRounds.length} rounds</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Entries List */}
      {entries.length > 0 ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Users className="text-[#1B3B6F]" />
              Event Entries
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Shooter</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Paid By</th>
                  <th className="text-center">Payment Date</th>
                  <th className="w-48">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id} data-testid={`payment-row-${entry.id}`}>
                    <td className="font-medium">{entry.shooter_name}</td>
                    <td className="text-center">
                      {entry.paid ? (
                        <span className="badge-paid" data-testid={`status-paid-${entry.id}`}>
                          <Check size={14} className="mr-1" />
                          Paid
                        </span>
                      ) : (
                        <span className="badge-unpaid" data-testid={`status-unpaid-${entry.id}`}>
                          <X size={14} className="mr-1" />
                          Unpaid
                        </span>
                      )}
                    </td>
                    <td className="text-center">
                      {entry.paid_by_sponsor && entry.sponsor_id ? (
                        <span className="inline-flex items-center gap-1 text-sm text-purple-700">
                          <Building2 size={14} />
                          {getSponsorName(entry.sponsor_id)}
                        </span>
                      ) : entry.paid ? (
                        <span className="text-slate-500">Self</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="text-center font-mono text-sm text-slate-500">
                      {entry.payment_date
                        ? formatDate(entry.payment_date)
                        : "-"}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <Button
                          variant={entry.paid ? "outline" : "default"}
                          size="sm"
                          onClick={() => togglePayment(entry.id, entry.paid)}
                          className={
                            entry.paid
                              ? "text-orange-600 hover:bg-orange-50"
                              : "bg-green-700 hover:bg-green-600"
                          }
                          data-testid={`toggle-payment-${entry.id}`}
                        >
                          {entry.paid ? (
                            <>
                              <X size={14} className="mr-1" />
                              Unpaid
                            </>
                          ) : (
                            <>
                              <Check size={14} className="mr-1" />
                              Paid
                            </>
                          )}
                        </Button>
                        {!entry.paid && sponsors.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSponsorDialog(entry)}
                            className="text-purple-600 hover:bg-purple-50"
                            data-testid={`sponsor-pay-${entry.id}`}
                          >
                            <Building2 size={14} className="mr-1" />
                            Sponsor
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      ) : selectedEvent ? (
        <Card data-testid="no-entries">
          <CardContent className="py-12 text-center">
            <DollarSign className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No entries for this event yet.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Select an event to view payments</p>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      {entries.length > 0 && (
        <div className="flex flex-wrap gap-4 justify-center text-sm">
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            Paid: {paidEntries.length}
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            Unpaid: {unpaidEntries.length}
          </span>
          <span className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-purple-500"></span>
            Sponsor Paid: {sponsorPaidEntries.length}
          </span>
        </div>
      )}

      {/* Sponsor Payment Dialog */}
      <Dialog open={sponsorDialogOpen} onOpenChange={setSponsorDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Sponsor Payment
            </DialogTitle>
            <DialogDescription>
              Mark this entry as paid by a sponsor
            </DialogDescription>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedEntry.shooter_name}</p>
                <p className="text-sm text-slate-500">
                  Entry Fee: ${selectedEventData?.entry_fee || 0}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Select Sponsor</label>
                <Select value={selectedSponsor} onValueChange={setSelectedSponsor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose sponsor" />
                  </SelectTrigger>
                  <SelectContent>
                    {sponsors.map((sponsor) => (
                      <SelectItem key={sponsor.id} value={sponsor.id}>
                        {sponsor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={markSponsorPaid}
                disabled={!selectedSponsor}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                <Building2 size={18} className="mr-2" />
                Mark as Sponsor Paid
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
